<template>
    <div>
        <span v-if="legalFileUrl"
            v-tooltip="$t('amas:analysis:pra:zk_extract:view')"
            class="btn btn-xs btn-outline btn-rounded btn-info view-pdf"
            :href="legalFileUrl"
            :data-title="lsData && lsData.mainBookName"
        >
            {{ $t("amas:analysis:pra:zk_extract") }}
        </span>

        <div class="vld-parent">
            <loading 
                :active="loading" 
                :can-cancel="false" 
                :is-full-page="false"
            />

            <ul class="search-listing">
                <li>
                    <div v-if="!loading && unitsData === null">
                        <template v-if="errorMsg">
                            {{ errorMsg }}
                        </template>
                        <template v-else>
                            {{ $t("task_details:status:no_data") }}
                        </template>
                    </div>
                    <div v-else v-for="(unit, idx) in unitsData" :key="'u-'+idx" class="row m-b-20">
                        <div :class="{
                                'col-md-8': showButton,
                                'col-md-12': !showButton,
                            }">
                            <h3>
                                <i class="mdi mdi-map"></i>
                                <span class="color-blue" :data-original-title="$t('real_estate:details')" rel="tooltip">
                                    {{ getUnitAddress(unit) }}
                                </span>
                            </h3>

                            <span class="re-key color-green" :data-original-title="$t('real_estate:details')" rel="tooltip">
                                {{ getUnitReKey(unit) }}
                            </span>

                            <div>
                                <div v-if="unit.size">{{ $t("general:area") }}: {{ unit.size | formatSize }}</div>
                                <div v-if="unit.unit_label">{{ $t("general:type") }}: {{ unit.unit_label }}</div>
                                <div v-if="unit.ownersDisplay">
                                    {{ $t("task_details:manage:owners") }}: {{ unit.ownersDisplay }}
                                </div>
                            </div>
                        </div>
                        <div v-if="showButton" class="col-md-4">
                            <div @click="clickButton(unit)">
                                <slot></slot>
                            </div>
                        </div>
                    </div>
                </li>

            </ul>

        </div>
    </div>
</template>

<script>
import Loading from "vue-loading-overlay"
import "vue-loading-overlay/dist/vue-loading.css";

import ApiService from "@/components/utils/api.service.js"
import Utils from "@/components/utils/utils.js";

export default {
    props: {
        rekey: String,
    },
    components: {
        Loading,
    },
    data() {
        return {
            loading: false,
            lsData: null,
            errorMsg: null,
        }
    },
    methods: {
        formatSize: Utils.formatSize,
        getUnitsData: async function() {
            this.loading = true;
            this.errorMsg = null;
            try {
                const parcelDetails = await ApiService.get(`/amas/real-estate/api/geoportal-parcel-details/${this.rekey}/`);
                const lrUnitId = this.getInternalUnitId(parcelDetails.data);
                if (!lrUnitId) {
                    this.errorMsg = this.$t("task_details:status:no_data");
                    this.lsData = null;
                    return;
                }
                const ldbData = await ApiService.get(`/amas/real-estate/api/geoportal-for-ldb-extract/${lrUnitId}/`);
                this.lsData = ldbData.data;
            } catch (err) {
                this.errorMsg = err?.response?.data?.detail || err?.message || this.$t("task_details:status:no_data");
                this.lsData = null;
            } finally {
                this.loading = false;
            }
        },
        getInternalUnitId: function(parcelDetails) {
            if (!parcelDetails) return null;
            if (parcelDetails.lrUnit?.lrUnitId) {
                return parcelDetails.lrUnit.lrUnitId;
            }
            const parcelLinks = parcelDetails.parcelLinks || [];
            const lrUnitsFromParcelLinks = parcelDetails.lrUnitsFromParcelLinks || [];
            if (parcelLinks.length > 0 && lrUnitsFromParcelLinks.length >= 1) {
                return parcelLinks[0]?.lrUnit?.lrUnitId || null;
            }
            return null;
        },
        clickButton: function(unit) {
            const data = {
                address: this.getUnitAddress(unit),
                re_key: this.getUnitReKey(unit),
                size: unit.size,
                custom_unit_id_for_zk: unit.custom_unit_id_for_zk,
                custom_share_id_for_zk: unit.custom_share_id_for_zk,
            };
            this.$emit("unit-on-parcel-clicked", data);
        },
        getUnitReKey: function(unit) {
            return unit.re_key;
        },
        getUnitAddress: function(unit) {
            return unit.address;
        },
    },
    computed: {
        unitsData: function() {
            if (!this.lsData?.ownershipSheetB?.lrUnitShares?.length) {
                return null;
            }
            const lrUnit = this.lsData;
            const cadParcel = lrUnit.possessionSheetA1?.cadParcels?.[0];
            const cadastralAddress = cadParcel?.address || "";
            const cadMunicipalityName = cadParcel?.cadMunicipalityName || "";

            return lrUnit.ownershipSheetB.lrUnitShares.map((share) => {
                const firstOwner = share.lrOwners?.[0];
                const ownerAddress = firstOwner?.address || "";
                const address = ownerAddress || (cadMunicipalityName && cadastralAddress ? `${cadMunicipalityName}, ${cadastralAddress}` : cadastralAddress || cadMunicipalityName);
                const orderNum = share.orderNumber ? String(share.orderNumber).replace(/\D/g, "") : "";
                const re_key = `${this.rekey}-${orderNum || share.lrUnitShareId}`;
                const typeMatch = share.description?.match(/\(E-\d+\)/);
                const unit_label = typeMatch ? `ETAŽNO VLASNIŠTVO ${typeMatch[0]}` : share.description || lrUnit.lrUnitTypeName;
                const ownersDisplay = (share.lrOwners || []).map((o) => o.name).filter(Boolean).join(", ");

                return {
                    address,
                    re_key,
                    size: null,
                    unit_label,
                    ownersDisplay,
                    custom_unit_id_for_zk: lrUnit.lrUnitId,
                    custom_share_id_for_zk: share.lrUnitShareId,
                };
            });
        },
        legalFileUrl: function() {
            if (!this.lsData?.fileUrl) return null;
            return `https://oss.uredjenazemlja.hr/oss/public/reports/${this.lsData.fileUrl}`;
        },
        showButton() {
            return this.$slots.default !== undefined;
        }
    },
    mounted() {
        this.getUnitsData();
    },
}
</script>

<style>
.color-blue {
    color: #2cabe3;
}
.color-green {
    color: #53e69d;
}
</style>
